import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Search, ChevronRight, List, Calendar, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UniversalLookup from './UniversalLookup';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const ClassificationDetail = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id || id === 'new';
    
    const [data, setData] = useState({
        classstructureid: null, 
        classificationid: '', 
        description: '', 
        parent: '',
        hierarchypath: '', 
        gen_description: true, 
        attributes: [], 
        use_with: []
    });
    
    const [selectedAttrIdx, setSelectedAttrIdx] = useState(0);
    const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '', targetTable: '' });
    const [fieldMaps] = useState({
        'uom': 'UOM',
        'datatype': 'DATATYPE'
    });

    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
    const { canCreate, canUpdate, canDelete } = usePermissions('classifications');
    const { fontSize } = useFontSize();

    useEffect(() => {
        if (id && id !== 'new') {
            const loadId = toast.loading(t('domains.toasts.loading'));
            axios.get(`/api/classifications/${id}`)
                .then(res => {
                    setData(res.data);
                    toast.dismiss(loadId);
                })
                .catch(err => toast.error(t('domains.toasts.load_fail'), { id: loadId }));
        }
    }, [id, t]);

    const handleSave = async () => {
        if (!data.classificationid) return toast.error(t('org_detail.toasts.id_required'));

        const payload = {
            ...data,
            parent: data.parent === "" ? null : parseInt(data.parent),
            attributes: data.attributes || [],
            use_with: data.use_with || []
        };

        const load = toast.loading(t('org_detail.toasts.saving'));
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/classifications/save', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(t('org_detail.toasts.success'), { id: load });
            
            if (res.data.hierarchypath) {
                setData(prev => ({ ...prev, hierarchypath: res.data.hierarchypath }));
            }

            if (id === 'new') navigate(`/classifications/${res.data.classstructureid || ''}`);
        } catch (err) {
            toast.error(t('org_detail.toasts.fail'), { id: load });
        }
    };

    const updateAttribute = (idx, field, value) => {
        const updated = [...data.attributes];
        updated[idx] = { ...updated[idx], [field]: value };
        setData({ ...data, attributes: updated });
    };

    const updateUseWith = (idx, field, value) => {
        const updated = [...data.use_with];
        updated[idx] = { ...updated[idx], [field]: value };
        setData({ ...data, use_with: updated });
    };

    const handleSmartSearch = (fieldName, titleKey, defaultType, targetTable = 'main') => {
        const linkedDomain = fieldMaps[fieldName];
        setLookup({ 
            isOpen: true, 
            title: t(`classifications.lookups.${titleKey}`), 
            type: linkedDomain ? 'DOMAIN' : defaultType, 
            domainId: linkedDomain || '', 
            field: fieldName,
            targetTable
        });
    };

    const handleParentSelect = (item) => {
        const parentId = item.classstructureid || item.id;
        const parentPath = item.hierarchypath || item.classificationid;
        
        setData(prev => ({
            ...prev,
            parent: parentId,
            hierarchypath: prev.classificationid ? `${parentPath} \\ ${prev.classificationid}` : parentPath
        }));
    };

    return (
        <div className={`min-h-screen bg-[#f3f4f6] pb-10 ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'} text-[0.9em]`} style={{ fontSize: `${fontSize}px` }}>
            {/* Header Toolbar */}
            <div className={`bg-[#1a3353] text-white p-2 flex justify-between items-center px-6 sticky top-0 z-50 shadow-lg border-b border-blue-400/20 `}>
                <div className={`flex items-center gap-4 text-[10px] font-black uppercase tracking-widest `}>
                    <button onClick={() => navigate('/classifications')} className="flex items-center gap-2 hover:text-blue-300 transition-colors">
                        <ArrowLeft size={14} className={isRTL ? 'rotate-180' : ''}/>
                        {t('sidebar.all_records')}
                    </button>
                    <ChevronRight size={14} className={`text-blue-400 ${isRTL ? 'rotate-180' : ''}`}/>
                    <span className="text-blue-100">{t('apps.CLASSIFICATION')}: {data.classificationid || t('sidebar.new')}</span>
                </div>
                <div className="flex gap-3">
                    {((isNew && canCreate) || (!isNew && canUpdate)) && (
                    <button onClick={handleSave} className="bg-blue-600 hover:bg-blue-500 px-6 py-1.5 rounded-sm flex items-center gap-2 font-black text-[10px] uppercase transition-all shadow-lg">
                        <Save size={14}/> {t('sidebar.save')}
                    </button>
                    )}
                </div>
            </div>

            <div className="max-w-7xl mx-auto mt-6 space-y-6 px-4">
                {/* 1. Main Info Section */}
                <div className="bg-white border p-8 shadow-xl grid grid-cols-3 gap-10 relative rounded-sm border-t-4 border-t-blue-600">
                    <div className="col-span-2 space-y-6">
                        <div className={`grid grid-cols-2 gap-x-16 gap-y-6 ${isRTL ? 'text-right' : 'text-left'}`}>
                            <EditableField 
                                label={t('apps.CLASSIFICATION')} 
                                value={data.classificationid} 
                                onChange={v => setData({...data, classificationid: v})} 
                                isHighlight isRequired isRTL={isRTL}
                            />
                            <EditableField 
                                label={t('organizations.description')} 
                                value={data.description} 
                                onChange={v => setData({...data, description: v})} 
                                isRTL={isRTL}
                            />
                            <EditableField 
                                label={t('classifications.hierarchy')} 
                                value={data.hierarchypath} 
                                isReadOnly isHighlight isRTL={isRTL}
                            />
                            <EditableField 
                                label={t('classifications.parent')} 
                                value={data.parent} 
                                onSearch={() => handleSmartSearch('parent', 'select_parent', 'classifications')} 
                                desc="ID" isRTL={isRTL}
                            />
                        </div>
                        <div className={`flex gap-12 mt-8 border-t pt-6`}>
                             <label className="flex items-center gap-3 text-[0.9em] font-black text-gray-500 uppercase cursor-pointer group">
                                <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={!!data.gen_description} onChange={e => setData({...data, gen_description: e.target.checked})} /> 
                                <span className="group-hover:text-blue-600 transition-colors">{t('classifications.gen_desc')}</span>
                             </label>
                             <label className="flex items-center gap-3 text-[0.9em] font-black text-gray-500 uppercase cursor-pointer">
                                <input type="checkbox" className="w-4 h-4 accent-green-600" checked={true} readOnly /> 
                                <span>{t('organizations.active')}</span>
                             </label>
                        </div>
                    </div>
                    <div className={`flex flex-col items-center justify-center border-gray-100 bg-gray-50/50 rounded-r-sm ${isRTL ? 'border-r pr-10' : 'border-l pl-10'}`}>
                        <div className="w-36 h-36 bg-white border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-300 rounded shadow-inner group cursor-pointer hover:border-blue-400 transition-all">
                             <Plus size={24} className="group-hover:text-blue-400 mb-2"/>
                             <span className="text-gray-400 font-black uppercase text-[8px] tracking-tighter">{t('classifications.upload_img')}</span>
                        </div>
                    </div>
                </div>

                {/* 2. Use With Table */}
                <div className="bg-white border shadow-md rounded-sm overflow-hidden">
                    <div className={`bg-gray-50 p-2 px-4 font-black text-[0.9em] text-gray-400 border-b flex justify-between items-center uppercase tracking-[0.9em]`}>
                        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <List size={14} className="text-blue-600"/>
                            <span>{t('classifications.use_with')}</span>
                        </div>
                        <button 
                            onClick={() => setData({...data, use_with: [...data.use_with, {objectname: '', description: '', top_level: false}]})}
                            className="bg-white border border-blue-200 text-blue-600 p-1 rounded hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                        >
                            <Plus size={14}/>
                        </button>
                    </div>
                    <table className="w-full text-[1.0em]">
                        <thead className="bg-gray-100/50 text-gray-500 border-b font-black uppercase text-[0.9em]">
                            <tr className={`${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                                <th className="p-2 w-10"></th>
                                <th className="p-2 border-r">{t('classifications.obj_name')}</th>
                                <th className="p-2 border-r">{t('organizations.description')}</th>
                                <th className="p-2 text-center w-24">{t('classifications.top_level')}</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.use_with.map((uw, i) => (
                                <tr key={i} className={`border-b group hover:bg-blue-50/30 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <td className="p-2 text-center text-blue-300"><ChevronRight size={12} className={isRTL ? 'rotate-180' : ''}/></td>
                                    <td className="p-2 border-r font-black text-blue-900 uppercase">
                                        <input className={`bg-transparent outline-none w-full ${isRTL ? 'text-right' : 'text-left'}`} value={uw.objectname} onChange={e => updateUseWith(i, 'objectname', e.target.value)} />
                                    </td>
                                    <td className="p-2 border-r text-gray-500">
                                        <input className={`bg-transparent outline-none w-full italic ${isRTL ? 'text-right' : 'text-left'}`} value={uw.description} onChange={e => updateUseWith(i, 'description', e.target.value)} />
                                    </td>
                                    <td className="p-2 text-center">
                                        <input type="checkbox" className="accent-blue-600" checked={!!uw.top_level} onChange={e => updateUseWith(i, 'top_level', e.target.checked)} />
                                    </td>
                                    <td className="p-2 text-center">
                                        <Trash2 size={13} className="text-gray-200 group-hover:text-red-500 cursor-pointer transition-colors mx-auto" 
                                               onClick={() => setData({...data, use_with: data.use_with.filter((_, idx) => idx !== i)})}/>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* 3. Attributes Table */}
                <div className="bg-white border shadow-xl rounded-sm overflow-hidden border-t-4 border-t-[#1a3353]">
                    <div className={`bg-[#1a3353] p-2.5 px-6 font-black text-[10px] text-white flex justify-between items-center uppercase tracking-widest`}>
                        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Calendar size={14} className="text-blue-400"/>
                            <span>{t('classifications.attributes')}</span>
                        </div>
                        <div className={`flex gap-6 items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <span className="font-bold text-blue-300 bg-blue-900/50 px-3 py-1 rounded-full text-[0.9em]">{t('dashboard.showing')}: {data.attributes.length}</span>
                            <button 
                                onClick={() => setData({...data, attributes: [...data.attributes, {assetattrid: '', description: '', datatype: 'ALN', uom: '', section: ''}]})}
                                className="bg-blue-600 p-1.5 rounded-full hover:rotate-90 transition-all shadow-lg active:scale-90"
                            >
                                <Plus size={16}/>
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[1.0em] border-collapse">
                            <thead className="bg-gray-50 text-gray-400 border-b font-black uppercase text-[0.9em]">
                                <tr className={`${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                                    <th className="p-3 border-r w-48">{t('classifications.attr_id')}</th>
                                    <th className="p-3 border-r">{t('organizations.description')}</th>
                                    <th className="p-3 text-center border-r w-24">{t('classifications.section')}</th>
                                    <th className="p-3 border-r w-32">{t('domains.domain_id')}</th>
                                    <th className="p-3 border-r w-24 text-center">{t('domains.type')}</th>
                                    <th className="p-3 border-r w-32">{t('classifications.uom')}</th>
                                    <th className="p-3 w-12 text-center"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {data.attributes.map((attr, i) => (
                                    <tr key={i} onClick={() => setSelectedAttrIdx(i)}
                                        className={`hover:bg-blue-50/50 cursor-pointer transition-colors ${isRTL ? 'flex-row-reverse text-right' : 'text-left'} ${selectedAttrIdx === i ? 'bg-blue-50/80 border-l-4 border-l-blue-600' : ''}`}>
                                        <td className="p-3 border-r font-black text-blue-900 underline uppercase tracking-tighter">{attr.assetattrid || '---'}</td>
                                        <td className="p-3 border-r text-gray-600 font-medium">{attr.description}</td>
                                        <td className="p-3 border-r text-gray-400 text-center font-mono">{attr.section}</td>
                                        <td className="p-3 border-r text-blue-700 font-bold uppercase">{attr.domainid}</td>
                                        <td className="p-3 border-r font-black uppercase text-gray-400 text-center text-[9px]">{attr.datatype}</td>
                                        <td className="p-3 border-r font-black text-blue-900 uppercase">{attr.uom}</td>
                                        <td className="p-3 text-center">
                                            <Trash2 size={13} className="text-gray-200 hover:text-red-600 transition-colors mx-auto" 
                                                   onClick={(e) => { e.stopPropagation(); setData({...data, attributes: data.attributes.filter((_, idx) => idx !== i)}) }}/>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Attribute Details Editor */}
                {data.attributes[selectedAttrIdx] && (
                    <div className="bg-white border p-10 grid grid-cols-2 gap-x-32 gap-y-6 animate-in slide-in-from-bottom-4 duration-500 shadow-2xl rounded-sm border-t-4 border-t-blue-500 relative">
                         <div className={`space-y-6 ${isRTL ? 'text-right order-2' : 'text-left'}`}>
                            <EditableField label={t('classifications.attr_id')} value={data.attributes[selectedAttrIdx].assetattrid} onChange={v => updateAttribute(selectedAttrIdx, 'assetattrid', v.toUpperCase())} onSearch={() => handleSmartSearch('assetattrid', 'select_attr', 'attributes', 'attr')} isHighlight isRTL={isRTL} />
                            <EditableField label={t('organizations.description')} value={data.attributes[selectedAttrIdx].description} onChange={v => updateAttribute(selectedAttrIdx, 'description', v)} isRTL={isRTL} />
                            <EditableField label={t('classifications.section')} value={data.attributes[selectedAttrIdx].section} onChange={v => updateAttribute(selectedAttrIdx, 'section', v)} isRTL={isRTL} />
                         </div>
                         <div className={`space-y-6 ${isRTL ? 'text-right order-1' : 'text-left'}`}>
                            <EditableField label={t('domains.type')} value={data.attributes[selectedAttrIdx].datatype} onChange={v => updateAttribute(selectedAttrIdx, 'datatype', v)} onSearch={() => handleSmartSearch('datatype', 'select_type', 'DOMAIN', 'attr')} isRTL={isRTL} />
                            <EditableField label={t('domains.domain_id')} value={data.attributes[selectedAttrIdx].domainid} onChange={v => updateAttribute(selectedAttrIdx, 'domainid', v)} onSearch={() => handleSmartSearch('domainid', 'select_domain', 'domains', 'attr')} isRTL={isRTL} />
                            <EditableField label={t('classifications.uom')} value={data.attributes[selectedAttrIdx].uom} onChange={v => updateAttribute(selectedAttrIdx, 'uom', v)} onSearch={() => handleSmartSearch('uom', 'select_uom', 'DOMAIN', 'attr')} isRTL={isRTL} />
                         </div>
                    </div>
                )}
            </div>

            <UniversalLookup 
                isOpen={lookup.isOpen}
                onClose={() => setLookup(prev => ({ ...prev, isOpen: false }))}
                title={lookup.title}
                type={lookup.type}
                domainId={lookup.domainId}
                onSelect={(item) => {
                    if (lookup.field === 'parent') {
                        handleParentSelect(item);
                        setLookup(prev => ({ ...prev, isOpen: false }));
                        return;
                    }
                    const val = item.value || item.id || item.assetattrid || item.classificationid;
                    if (lookup.targetTable === 'attr') {
                        updateAttribute(selectedAttrIdx, lookup.field, val);
                        if (item.description && lookup.field === 'assetattrid') updateAttribute(selectedAttrIdx, 'description', item.description);
                    } else {
                        setData(prev => ({ ...prev, [lookup.field]: val }));
                    }
                    setLookup(prev => ({ ...prev, isOpen: false }));
                }}
            />
        </div>
    );
};

// --- Helpers ---

const EditableField = ({ label, value, onChange, onSearch, isRequired, isReadOnly, isHighlight, desc }) => (
    <div className="flex flex-col gap-1 border-b border-gray-100 group transition-all focus-within:border-blue-500 pb-1">
        <label className={`${isRequired ? 'text-red-700' : 'text-gray-400'} font-black text-[0.8em] uppercase tracking-[0.2em]`}>
            {isRequired && '* '} {label}
        </label>
        <div className="flex items-center justify-between">
            <input 
                readOnly={isReadOnly}
                spellCheck="false"
                className={`outline-none font-black bg-transparent w-full text-[0.9em] uppercase tracking-tighter ${isHighlight ? 'text-blue-600' : 'text-blue-900'} ${isReadOnly ? 'cursor-not-allowed opacity-50 font-bold' : ''}`} 
                value={value || ''} 
                onChange={e => onChange && onChange(e.target.value)} 
                placeholder="..."
            />
            {desc && <span className="text-gray-300 font-bold px-2 italic text-[0.9em] whitespace-nowrap lowercase">{desc}</span>}
            <div className="flex gap-2 text-blue-600 cursor-pointer pl-2">
                {onSearch && !isReadOnly && (
                    <div className="p-1 hover:bg-blue-50 rounded-full transition-colors" onClick={onSearch}>
                        <Search size={12} className="text-blue-500" />
                    </div>
                )}
            </div>
        </div>
    </div>
);

export default ClassificationDetail;